import { useI18n } from '@/lib/i18n';
import { Currency } from '@/types/inertia-props';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { usePage } from '@inertiajs/react';
import 'node_modules/flag-icons/css/flag-icons.min.css';
import { useMemo, useState } from 'react';

export default function LocaleSwitcher({ reversed = false }: { reversed?: boolean }) {
  const { t } = useI18n();

  const { props } = usePage();
  const [isOpen, setIsOpen] = useState(false);

  const icon = useMemo(() => {
    return props.supportedLocales.find((l) => l.key === props.locale)!.icon;
  }, [props.locale]);

  function addCurrencyToUrl(currency: Currency) {
    const url = new URL(window.location.href);
    url.searchParams.set('currency', currency.code);
    return url.toString();
  }

  return (
    <>
      {isOpen && <div className="fixed inset-0 z-0" onClick={() => setIsOpen(false)} />}

      <div className="relative inline-block text-left">
        <div>
          <button
            id="locale-switcher"
            type="button"
            className="inline-flex items-center justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold shadow-xs ring-1 ring-gray-300 ring-inset hover:bg-gray-50"
            onClick={() => setIsOpen((prev) => !prev)}
          >
            <span className={`fi fi-${icon}`} />
            <span className={'rounded bg-gray-100 px-1 text-xs text-gray-700'}>{props.currency.code}</span>
            <ChevronDownIcon className={'size-5 text-gray-500'} />
          </button>
        </div>

        {isOpen && (
          <div
            className={`absolute left-0 z-10 my-2 w-48 rounded-md bg-white shadow-lg ring-1 ring-black/5 sm:right-0 sm:left-auto ${reversed ? 'bottom-full origin-bottom-right' : 'top-full origin-top-right'}`}
          >
            <div className="rounded-t-md bg-gray-50 p-2 text-xs font-semibold">{t('layout.Change language')}</div>

            <div className="py-1">
              {props.supportedLocales.map((locale) => (
                <a
                  key={locale.key}
                  id={`language-${locale.key}`}
                  href={locale.alternateUrl}
                  className="group flex items-center gap-x-1.5 px-4 py-2 text-sm text-gray-900 capitalize"
                >
                  <span className={`fi fi-${locale.icon}`} />
                  <span className="group-hover:underline">{locale.text}</span>
                </a>
              ))}
            </div>

            <div className="bg-gray-50 p-2 text-xs font-semibold">{t('layout.Change currency')}</div>
            <div className="grid grid-cols-2 py-1">
              {props.supportedCurrencies.map((currency) => (
                <a
                  key={currency.code}
                  id={`currency-${currency.code}`}
                  href={addCurrencyToUrl(currency)}
                  className="flex items-center gap-x-1.5 px-4 py-2 text-sm text-gray-900 capitalize hover:underline"
                >
                  {currency.code} <span className={'rounded-full bg-gray-200 px-1.5 text-xs font-semibold text-gray-900'}>{currency.sign}</span>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
